import { chainableEditorState, findParentNode, ExtensionTag, extensionDecorator, NodeExtension, convertCommand, isElementDomNode, Preset, presetDecorator } from '@remirror/core';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { wrappingInputRule } from '@remirror/pm/inputrules';
import { liftListItem, wrapInList, splitListItem, sinkListItem } from '@remirror/pm/schema-list';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';

/**
 * Checks to see whether this is a list node.
 */

function isList(node, schema) {
  var _node$type$spec$group;

  return ((_node$type$spec$group = node.type.spec.group) === null || _node$type$spec$group === void 0 ? void 0 : _node$type$spec$group.includes(ExtensionTag.ListContainerNode)) || node.type === schema.nodes.bulletList || node.type === schema.nodes.orderedList;
}
/**
 * Toggles a list item.
 *
 * @remarks
 *
 * When the provided list wrapper is inactive (e.g. ul) then wrap the list with
 * this type. When it is active then remove the selected line from the list.
 *
 * @param type - the list node type
 * @param itemType - the list item type (must be in the schema)
 */


function toggleList(type, itemType) {
  return parameter => {
    var dispatch = parameter.dispatch,
        tr = parameter.tr;
    var state = chainableEditorState(tr, parameter.state);
    var schema = state.schema;
    var selection = tr.selection;
    var $from = selection.$from,
        $to = selection.$to;
    var range = $from.blockRange($to);

    if (!range) {
      return false;
    }

    var parentList = findParentNode({
      predicate: node => isList(node, schema),
      selection
    });

    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
      if (parentList.node.type === type) {
        return liftListItem(itemType)(state, dispatch);
      }

      if (isList(parentList.node, schema) && type.validContent(parentList.node.content)) {
        if (dispatch) {
          dispatch(tr.setNodeMarkup(parentList.pos, type));
        }

        return true;
      }
    }

    return wrapInList(type)(state, dispatch);
  };
}

var _dec, _class, _temp;
/**
 * Creates the node for a bullet list.
 */

var BulletListExtension = (_dec = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec(_class = (_temp = class BulletListExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.BlockNode, ExtensionTag.ListContainerNode]);
  }

  get name() {
    return 'bulletList';
  }

  createNodeSpec(extra) {
    return {
      attrs: extra.defaults(),
      content: 'listItem+',
      parseDOM: [{
        tag: 'ul',
        getAttrs: extra.parse
      }],
      toDOM: node => ['ul', extra.dom(node), 0]
    };
  }

  createCommands() {
    return {
      /**
       * Toggle the bullet list.
       */
      toggleBulletList: () => toggleList(this.type, this.store.schema.nodes.listItem)
    };
  }

  createKeymap() {
    return {
      'Shift-Ctrl-8': toggleList(this.type, this.store.schema.nodes.listItem)
    };
  }

  createInputRules() {
    return [wrappingInputRule(/^\s*([*+-])\s$/, this.type)];
  }

}, _temp)) || _class);

var _dec$1, _class$1, _temp$1;
/**
 * Creates the node for a list item.
 */

var ListItemExtension = (_dec$1 = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec$1(_class$1 = (_temp$1 = class ListItemExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.ListItemNode]);
  }

  get name() {
    return 'listItem';
  }

  createNodeSpec(extra) {
    return {
      attrs: extra.defaults(),
      content: 'paragraph block*',
      defining: true,
      draggable: false,
      parseDOM: [{
        tag: 'li',
        getAttrs: extra.parse
      }],
      toDOM: node => ['li', extra.dom(node), 0]
    };
  }

  createKeymap() {
    return {
      Enter: convertCommand(splitListItem(this.type)),
      Tab: convertCommand(sinkListItem(this.type)),
      'Shift-Tab': convertCommand(liftListItem(this.type))
    };
  }

}, _temp$1)) || _class$1);

var _dec$2, _class$2, _temp$2;
/**
 * Creates the list for the ordered list.
 */

var OrderedListExtension = (_dec$2 = /*#__PURE__*/extensionDecorator({}), /*#__PURE__*/_dec$2(_class$2 = (_temp$2 = class OrderedListExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.BlockNode, ExtensionTag.ListContainerNode]);
  }

  get name() {
    return 'orderedList';
  }

  createNodeSpec(extra) {
    return {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        order: {
          default: 1
        }
      }),
      content: 'listItem+',
      parseDOM: [{
        tag: 'ol',
        getAttrs: node => {
          var _node$getAttribute;

          if (!isElementDomNode(node)) {
            return {};
          }

          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            order: +((_node$getAttribute = node.getAttribute('start')) !== null && _node$getAttribute !== void 0 ? _node$getAttribute : 1)
          });
        }
      }],
      toDOM: node => {
        var extraAttributes = extra.dom(node);
        return node.attrs.order === 1 ? ['ol', extraAttributes, 0] : ['ol', _objectSpread(_objectSpread({}, extraAttributes), {}, {
          start: node.attrs.order
        }), 0];
      }
    };
  }

  createCommands() {
    return {
      /**
       * Toggle the ordered list for the current selection.
       */
      toggleOrderedList: () => toggleList(this.type, this.store.schema.nodes.listItem)
    };
  }

  createKeymap() {
    return {
      'Shift-Ctrl-9': toggleList(this.type, this.store.schema.nodes.listItem)
    };
  }

  createInputRules() {
    return [wrappingInputRule(/^(\d+)\.\s$/, this.type, match => ({
      order: +match[1]
    }), (match, node) => node.childCount + node.attrs.order === +match[1])];
  }

}, _temp$2)) || _class$2);

var _dec$3, _class$3;
var ListPreset = (_dec$3 = /*#__PURE__*/presetDecorator({}), /*#__PURE__*/_dec$3(_class$3 = class ListPreset extends Preset {
  get name() {
    return 'list';
  }

  onSetOptions(_) {}

  createExtensions() {
    return [new OrderedListExtension(), new BulletListExtension(), new ListItemExtension()];
  }

}) || _class$3);

export { BulletListExtension, ListItemExtension, ListPreset, OrderedListExtension };
